import React from 'react';
import { motion } from 'framer-motion';
import { ArrowsOutSimple, ArrowsInSimple, ArrowLeft, X, ClockCounterClockwise } from '@phosphor-icons/react';

import { Button } from '../ui/button';
import { useIsChatSearchDirty } from './hooks/useIsChatSearchDirty';
import { useChatSearch } from './ChatSearchContext';

import { cn } from '@/lib/utils';

type HeaderProps = React.HTMLAttributes<HTMLDivElement> & {
  headerText: string;
  onToggleSizeButtonClick: () => void;
  onBackButtonClick: () => void;
  onCloseWidgetClick: () => void;
  onHistoryButtonClick: () => void;
  onHistoryBackClick?: () => void;
  showHistory?: boolean;
  isSearchWidget?: boolean;
  hasUnreadConversations?: boolean;
  showHistoryButton?: boolean;
};

export function Header({
  headerText,
  onToggleSizeButtonClick,
  onBackButtonClick,
  onCloseWidgetClick,
  isSearchWidget,
  onHistoryButtonClick,
  onHistoryBackClick,
  showHistory,
  hasUnreadConversations,
  className,
  showHistoryButton,
  ...props
}: HeaderProps) {
  const isDirty = useIsChatSearchDirty();
  const [isExpanded, setIsExpanded] = React.useState(false);

  const { isSearching, showMobileCloseWidgetButton: hideExpandButton } = useChatSearch();

  const handleToggleSizeButtonClick = () => {
    setIsExpanded((prev) => !prev);
    onToggleSizeButtonClick();
  };

  return (
    <div
      className={cn(
        'w-full px-3 py-4 flex items-center justify-between',
        'bg-primary h-14 leading-6',
        'md:hidden',
        '@3xl:bg-transparent @3xl:h-auto @3xl:leading-normal',
        '@3xl:justify-center @3xl:space-y-4 @3xl:p-0',
        isSearchWidget ? 'hidden bg-transparent' : '',
        className,
      )}
      {...props}
    >
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className={cn(
          'm-0 text-primary-foreground @3xl:text-foreground flex gap-1 items-center font-bold text-base',
          isSearchWidget ? '' : '@3xl:text-5xl',
        )}
      >
        {(showHistory || isDirty) && (
          <Button
            size="icon"
            variant="ghost"
            onClick={showHistory ? onHistoryBackClick : onBackButtonClick}
            aria-label="Go back"
            className={cn('shrink-0 text-primary-foreground', isSearchWidget ? '' : '@3xl:hidden')}
            disabled={isSearching}
          >
            <ArrowLeft />
          </Button>
        )}

        <span>{showHistory ? 'History' : headerText}</span>
      </motion.h2>
      <div className="@3xl:hidden shrink-0" data-name="HeaderButtons">
        {!hideExpandButton && (
          <Button
            size="icon"
            variant="ghost"
            onClick={handleToggleSizeButtonClick}
            aria-label={isExpanded ? 'Collapse widget' : 'Expand widget'}
            className="text-primary-foreground device-touch:hidden device-desktop:inline-flex device-desktop-touch:inline-flex"
          >
            {isExpanded ? <ArrowsInSimple /> : <ArrowsOutSimple />}
          </Button>
        )}
        {showHistoryButton && (
          <Button
            size="icon"
            variant="ghost"
            onClick={onHistoryButtonClick}
            aria-label="Conversation history"
            className="relative text-primary-foreground"
          >
            <ClockCounterClockwise />
            {hasUnreadConversations && !showHistory && (
              <span className="absolute top-1 right-1 size-2 rounded-full bg-red-500" />
            )}
          </Button>
        )}
        <Button
          size="icon"
          variant="ghost"
          onClick={onCloseWidgetClick}
          aria-label="Close widget"
          className="text-primary-foreground"
        >
          <X />
        </Button>
      </div>
    </div>
  );
}
